<script setup lang="ts">
import { watchEffect, computed, useTemplateRef } from 'vue';
import { UiText, useSharedLink, type ShopexpertsPreviewExpert } from '@shopexperts/shared';
import useProfileDetails from './composables/useProfileDetails';
import type z from 'zod';
import type { OverallReviewsSchema } from '@shopexperts/shared';

const PROFILE_PREVIEW_URL = import.meta.env.VITE_PROFILE_PREVIEW_URL || 'http://localhost:3000/freelancer-preview'
const PROFILE_PREVIEW_ORIGIN = new URL(PROFILE_PREVIEW_URL, window.location.origin).origin

const previewIframe = useTemplateRef('previewIframe');

interface PreviewProps {
    body: ShopexpertsPreviewExpert;
}

const { data: profile } = useSharedLink<ShopexpertsPreviewExpert>({
    iframeRef: previewIframe as any, // TODO: fix this
    name: 'shopexperts-service',
    mode: 'providerToClient',
    targetOrigin: PROFILE_PREVIEW_ORIGIN
})

const { data: reviews } = useSharedLink<z.infer<typeof OverallReviewsSchema>>({
    iframeRef: previewIframe as any, // TODO: fix this
    name: 'overall-reviews',
    mode: 'providerToClient',
    targetOrigin: PROFILE_PREVIEW_ORIGIN
})

const { overallReviews } = useProfileDetails();

const props = defineProps<PreviewProps>();

const body = computed(() => props.body);

watchEffect(() => {
    profile.value = {
        profile: body.value.profile,
        offered_services: body.value.offered_services,
        packaged_services: body.value.packaged_services,
        customer_stories: body.value.customer_stories,
        faqs: body.value.faqs,
        badges: body.value.badges,
    }
})

watchEffect(() => {
    reviews.value = {
        overall: {
            rating: overallReviews.value?.overall?.rating ?? 0,
            reviews_count: overallReviews.value?.overall?.reviews_count ?? 0
        },
        breakdown: {
            quality_of_work: overallReviews.value?.breakdown?.quality_of_work ?? 0,
            communication: overallReviews.value?.breakdown?.communication ?? 0,
            respects_timelines: overallReviews.value?.breakdown?.respects_timelines ?? 0,
            value_for_money: overallReviews.value?.breakdown?.value_for_money ?? 0
        }
    };
})
</script>

<template>
    <div class="flex flex-col w-full h-full">
        <div class="w-full bg-se-dark text-se-white p-1 text-center font-semibold flex-shrink-0">
            <UiText as="h6" text="This is a preview of your profile with all the unsaved changes, and all PRO features unlocked. This is not the live listing." />
        </div>
        <iframe :src="PROFILE_PREVIEW_URL" ref="previewIframe" class="w-full flex-1" />
    </div>
</template>
